import {
  createEmbeddingContext,
  type DashboardExperience,
  type EmbeddingEvents,
  InfoMessageEventName,
} from "amazon-quicksight-embedding-sdk";
import { useEffect, useRef, useState } from "react";

type UseAnalyticsDashboardEmbedOptions = {
  embedUrl?: string;
  darkThemeArn?: string;
  lightThemeArn?: string;
  theme: "dark" | "light";
  disabled?: boolean;
};

function getEmbedErrorMessage(event: EmbeddingEvents): string {
  const errorCode =
    typeof event.message === "object" &&
    event.message !== null &&
    "errorCode" in event.message &&
    typeof event.message.errorCode === "string"
      ? event.message.errorCode
      : null;

  return errorCode
    ? `QuickSight failed to render the dashboard (${errorCode}).`
    : "QuickSight failed to render the dashboard.";
}

export function useAnalyticsDashboardEmbed({
  embedUrl,
  darkThemeArn,
  lightThemeArn,
  theme,
  disabled = false,
}: UseAnalyticsDashboardEmbedOptions) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dashboardRef = useRef<DashboardExperience | null>(null);
  const currentThemeRef = useRef(theme);
  const [isDashboardLoaded, setIsDashboardLoaded] = useState(false);
  const [embedError, setEmbedError] = useState<string | null>(null);

  useEffect(() => {
    currentThemeRef.current = theme;
  }, [theme]);

  useEffect(() => {
    if (!embedUrl || !containerRef.current || disabled) {
      return;
    }

    let isActive = true;
    const container = containerRef.current;
    const initialThemeArn =
      currentThemeRef.current === "dark" ? darkThemeArn : lightThemeArn;

    dashboardRef.current = null;
    container.replaceChildren();
    setIsDashboardLoaded(false);
    setEmbedError(null);

    const handleMessage = (event: EmbeddingEvents) => {
      if (!isActive) {
        return;
      }

      if (event.eventName === InfoMessageEventName.CONTENT_LOADED) {
        setIsDashboardLoaded(true);
        return;
      }

      if (event.eventName === InfoMessageEventName.ERROR_OCCURRED) {
        setEmbedError(getEmbedErrorMessage(event));
      }
    };

    void (async () => {
      try {
        const embeddingContext = await createEmbeddingContext();

        if (!isActive) {
          return;
        }

        const dashboard = await embeddingContext.embedDashboard(
          {
            url: embedUrl,
            container,
            className: "tw:h-full tw:w-full",
            width: "100%",
            height: "100%",
            resizeHeightOnSizeChangedEvent: true,
            withIframePlaceholder: false,
          },
          {
            onMessage: handleMessage,
            themeOptions: initialThemeArn
              ? {
                  themeArn: initialThemeArn,
                }
              : undefined,
          },
        );

        if (!isActive) {
          return;
        }

        dashboardRef.current = dashboard;
      } catch (error) {
        if (!isActive) {
          return;
        }

        setEmbedError(
          error instanceof Error
            ? error.message
            : "Unable to embed the dashboard right now.",
        );
      }
    })();

    return () => {
      isActive = false;
      dashboardRef.current = null;
      container.replaceChildren();
    };
  }, [disabled, embedUrl, lightThemeArn, darkThemeArn]);

  useEffect(() => {
    if (!isDashboardLoaded || !dashboardRef.current) {
      return;
    }

    const themeArn = theme === "dark" ? darkThemeArn : lightThemeArn;

    if (!themeArn) {
      return;
    }

    void dashboardRef.current.setTheme(themeArn).catch((error: unknown) => {
      setEmbedError(
        error instanceof Error
          ? error.message
          : "Unable to update the dashboard theme right now.",
      );
    });
  }, [theme, darkThemeArn, lightThemeArn, isDashboardLoaded]);

  return {
    containerRef,
    embedError,
    isDashboardLoaded,
    reset: () => {
      setIsDashboardLoaded(false);
      setEmbedError(null);
    },
  };
}
